import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, ButtonBase, IconButton, Tooltip } from '@mui/material';
import Card from '@mui/material/Card';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import { usePageContext } from 'providers/page';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { pageHeight } from 'utils/layout';
import { readBooleanPref, writeBooleanPref } from 'utils/local-storage';

/** Shared by every page that opts in, so collapsing it once keeps it collapsed. */
const COLLAPSED_KEY = 'aktify_metadata_collapsed';
const RAIL_WIDTH = 40;

interface MetadataProps {
  collapsible?: boolean;
}

export const Metadata: FC<PropsWithChildren<MetadataProps>> = ({
  children,
  collapsible = false,
}) => {
  const [isBodyScrollable, setIsBodyScrollable] = useState(false);
  const [collapsedPref, setCollapsedPref] = useState(() => readBooleanPref(COLLAPSED_KEY));
  const { fullHeight } = usePageContext();

  const collapsed = collapsible && collapsedPref;

  const checkScrollbar = () => {
    const hasVerticalScrollbar = document.documentElement.scrollHeight > window.innerHeight;
    setIsBodyScrollable(hasVerticalScrollbar);
  };

  useEffect(() => {
    // On full-height routes this is dead, and a forced reflow on every resize tick.
    if (fullHeight) return;

    checkScrollbar();
    window.addEventListener('resize', checkScrollbar);

    return () => {
      window.removeEventListener('resize', checkScrollbar);
    };
  }, [fullHeight, collapsed]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsedPref(next);
    writeBooleanPref(COLLAPSED_KEY, next);
  };

  const paperSizing = fullHeight
    ? // The shell already bounds the row, so the panel just fills it and scrolls.
      { height: '100%', overflowY: 'auto' as const }
    : isBodyScrollable
    ? // Body scrolls - drawer grows with content
      { height: 'auto', minHeight: '100vh', overflowY: 'visible' as const }
    : // Body doesn't scroll - drawer has fixed height and scrolls
      { height: pageHeight(), overflowY: 'auto' as const };

  // Expanded values are the pre-existing ones: the paper clamps to its container, the root does not.
  const rootWidth = collapsed
    ? { minWidth: RAIL_WIDTH, maxWidth: RAIL_WIDTH }
    : { minWidth: '250px', maxWidth: '400px' };
  const paperWidth = collapsed ? rootWidth : { minWidth: '250px', maxWidth: 'min(100%, 400px)' };

  return (
    <>
      <Drawer
        data-testid="metadata-drawer"
        variant="permanent"
        anchor="right"
        sx={{
          flexShrink: 1,
          ...rootWidth,
          display: { xs: 'none', md: 'flex' },
        }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '#F3F3F3',
              position: 'relative',
              ...paperWidth,
              ...paperSizing,
              overflowX: 'hidden',
            },
          },
        }}
        open
      >
        {collapsed ? (
          <ButtonBase
            aria-label="Show explanations"
            onClick={toggleCollapsed}
            sx={{ flexDirection: 'column', gap: 1.25, paddingY: 1.25, alignItems: 'center' }}
          >
            <ChevronLeftIcon fontSize="small" color="action" />
            <InfoOutlinedIcon fontSize="small" color="action" />
            <Box
              component="span"
              sx={{
                writingMode: 'vertical-rl',
                fontSize: 13,
                fontWeight: 500,
                letterSpacing: '0.02857em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              Explanations
            </Box>
          </ButtonBase>
        ) : (
          <>
            {collapsible && (
              <Box display="flex" justifyContent="flex-end" paddingTop={0.5} paddingRight={0.5}>
                <Tooltip title="Hide explanations">
                  <IconButton aria-label="Hide explanations" size="small" onClick={toggleCollapsed}>
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <List>{children}</List>
          </>
        )}
      </Drawer>
      <Card
        variant="outlined"
        sx={{ margin: '16px', padding: '16px', flex: 1, display: { xs: 'flex', md: 'none' } }}
      >
        <List sx={{ width: '100%' }}>{children}</List>
      </Card>
    </>
  );
};
